import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useEffect, useState } from "react";
import { isConfigured } from "../../shared/connectors/isDraft";
import { sharedMutators } from "../../shared/mutators";
import type { Connector } from "../lib/connector";
import { formatRelativeTime } from "../lib/formatRelativeTime";
import { StatusDot } from "./StatusDot";
import { TestConnectionButton } from "./TestConnectionButton";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";

const Container = styled("div")`
  display: flex;
  flex-direction: row;
  align-items: center;
  background: var(--surface-primary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 16px;
  overflow: hidden;
`;

const Pill = styled("div")`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 6px 8px;
  gap: 4px;
`;

type Props = {
  app: Connector;
};

export function ConnectorStatusPill({ app }: Props) {
  const z = useZero();
  const configured = isConfigured(app);
  const isPending = app.lastTestStatus === "pending";

  const [renderedStatus, setRenderedStatus] = useState(app.lastTestStatus);
  useEffect(() => {
    if (!["pending", renderedStatus].includes(app.lastTestStatus))
      // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
      setRenderedStatus(app.lastTestStatus);
  }, [app.lastTestStatus, renderedStatus]);
  const status = renderedStatus;
  const untested = !status || status === "pending";

  const color = !configured
    ? "orange"
    : isPending || untested
      ? "gray"
      : status === "failure"
        ? "red"
        : "green";
  const label = !configured
    ? "Draft"
    : status === "failure"
      ? "Error"
      : untested
        ? "Untested"
        : "Active";

  const errorMessage =
    status === "failure" && app.lastTestMessage ? app.lastTestMessage : null;

  const content = (
    <Container>
      <Pill>
        <StatusDot color={color} />
        <Text
          variant="text-small-md"
          color={isPending ? "var(--text-secondary)" : "var(--text-primary)"}
        >
          {label}
        </Text>
      </Pill>
      {configured && (
        <TestConnectionButton
          running={isPending}
          onTest={() =>
            z.mutate(
              sharedMutators.testConnectedApp({ connectedAppId: app.id })
            )
          }
        />
      )}
    </Container>
  );

  const tooltip = errorMessage
    ? errorMessage
    : configured && !isPending && app.lastTestAt
      ? `Last tested ${formatRelativeTime(app.lastTestAt)}`
      : null;

  if (tooltip) {
    return (
      <Tooltip content={tooltip} selectable={!!errorMessage}>
        {content}
      </Tooltip>
    );
  }

  return content;
}
